import { useMemo } from 'react';
import { PAYMENT_MODE_OPTIONS } from '../constants';

// Medical Transaction Type Options (only PO and Sales)
const MEDICAL_TRANSACTION_TYPE_OPTIONS = [
  { label: 'Purchase Order', value: 'po' },
  { label: 'Sale', value: 'sell' },
] as const;

export const useTransactionsFormFields = (
  initialValues?: any,
  transactionType?: string,
  onTransactionTypeChange?: (value: string) => void
) => {
  return useMemo(() => {

    const currentTransactionType =
      transactionType || initialValues?.transaction_type;

    const allFields = [
      // Row 1: Transaction Type + Reference Selector
      {
        name: 'transaction_type',
        label: 'Transaction Type',
        type: 'select',
        required: true,
        options: MEDICAL_TRANSACTION_TYPE_OPTIONS,
        placeholder: 'Select transaction type',
        layout: { span: 12 }, // Half width for row layout
        defaultValue: initialValues?.transaction_type,
        onChange: (value: string, form: any) => {
          // Handle transaction type change
          const currentValues = form.getFieldsValue();
          const preservedValues: any = {};
          
          // Preserve relevant values based on transaction type
          if (value === 'purchase') {
            preservedValues.po_id = currentValues.po_id;
            preservedValues.amount = currentValues.amount;
          } else if (value === 'sale') {
            preservedValues.s_id = currentValues.s_id;
            preservedValues.amount = currentValues.amount;
          }
          
          // Clear all reference fields
          const fieldsToClear = ['po_id', 's_id', 'amount', 'payment_mode', 'payment_status'];
          const clearedFields: any = {};
          fieldsToClear.forEach(field => {
            if (field !== 'amount') { // Keep amount for both types
              clearedFields[field] = null;
            }
          });
          
          // Set preserved values and clear others
          form.setFieldsValue({
            ...clearedFields,
            ...preservedValues
          });
        },
      },
      {
        name: 'po_id',
        label: 'Purchase Order ID',
        type: 'resourceSelect',
        required: true,
        placeholder: 'Search and select purchase order',
        layout: { span: 12 }, // Half width to be on same row as transaction type
        defaultValue: initialValues?.po_id,
        searchSelectProps: {
          initialValue: initialValues?.po_id ? initialValues.po_id : undefined,
          resource: 'purchase-orders',
          optionValue: 'id',
          searchFields: ['id', 'supplier_name', 'invoice_no'],
          searchDebounceMs: 300,
          optionRender: (po: any) =>
            `PO #${po.id} - ${po.supplier?.name || 'Unknown Supplier'} - ₹${Number(po.due_amount || 0).toFixed(2)} due`,
          allowClear: true,
          filters: [],
          sorters: [{ field: 'id', order: 'desc' }],
        },
        onChange: (value: any, form: any) => {
          // Auto-fill amount when PO is selected
          // Ensure `value` is an object before trying to access `value.data`
          const selectedPo = value?.data || value;
          if (selectedPo && form && typeof form.setFieldsValue === 'function') {
            const dueAmount = Number(selectedPo.due_amount || 0);
            if (dueAmount > 0) {
              form.setFieldsValue({ amount: dueAmount });
            }
          }
        },
      },
      {
        name: 's_id',
        label: 'Sale ID',
        type: 'resourceSelect',
        required: true,
        placeholder: 'Search and select sale',
        layout: { span: 12 }, // Half width to be on same row as transaction type
        defaultValue: initialValues?.s_id,
        searchSelectProps: {
          initialValue: initialValues?.s_id ? initialValues.s_id : undefined,
          resource: 'sales',
          optionValue: 'id',
          searchFields: ['id', 'customer_name', 'customer_phone'],
          searchDebounceMs: 300,
          optionRender: (sale: any) =>
            `Sale #${sale.id} - ${sale.customer_name || 'Unknown Customer'} - ₹${Number(sale.due_amount || 0).toFixed(2)} due`,
          allowClear: true,
        },
        onChange: (value: any, form: any) => {
          // Auto-fill amount when Sale is selected
          // Ensure `value` is an object before trying to access `value.data`
          const selectedSale = value?.data || value;
          if (
            selectedSale &&
            form &&
            typeof form.setFieldsValue === 'function'
          ) {
            const dueAmount = Number(selectedSale.due_amount || 0);
            if (dueAmount > 0) {
              form.setFieldsValue({ amount: dueAmount });
            }
          }
        },
      },
      // Row 2: Payment Mode + Amount
      {
        name: 'payment_mode',
        label: 'Payment Mode',
        type: 'select',
        required: true,
        options: PAYMENT_MODE_OPTIONS,
        placeholder: 'Select payment mode',
        layout: { span: 12 },
      },
      {
        name: 'amount',
        label: 'Amount',
        type: 'number',
        required: true,
        placeholder: 'Enter amount',
        layout: { span: 12 },
        min: 0,
        step: 0.01,
      },
    ];

    // Use hidden property to conditionally show/hide fields
    return allFields.map((field: any) => {
      // For po_id: hide if transaction_type is not 'po'
      if (field.name === 'po_id') {
        return {
          ...field,
          hidden: currentTransactionType !== 'po',
        };
      }

      // For s_id: hide if transaction_type is not 'sell'
      if (field.name === 's_id') {
        return {
          ...field,
          hidden: currentTransactionType !== 'sell',
        };
      }

      return field;
    });
  }, [initialValues, transactionType]);
};
